import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Sun, Zap, Home, Building, Battery, Layers,
  Map, Search, Layout, FileText, CheckCircle, DollarSign,
  Settings, Droplet, Wrench, Activity, Grid, Cpu, BatteryCharging, Box
} from 'lucide-react';
import { mockData } from '../mock';

const Services = () => {
  const [activeTab, setActiveTab] = useState('installation');

  const ServiceCard = ({ service, IconComponent }) => (
    <Card className="relative border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group bg-white overflow-hidden">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-lime-200 to-green-200 rounded-bl-full opacity-50 group-hover:opacity-70 transition-opacity"></div>
      
      <CardHeader className="relative z-10">
        <div className="w-14 h-14 bg-gradient-to-br from-lime-500 to-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
          <IconComponent className="w-7 h-7 text-white" />
        </div>
        <CardTitle className="text-base font-bold text-gray-900 group-hover:text-green-700 transition-colors leading-tight">
          {service.title}
        </CardTitle>
      </CardHeader>
      
      <CardContent className="relative z-10">
        <p className="text-gray-600 text-sm leading-relaxed">
          {service.description}
        </p>
      </CardContent>
    </Card>
  );

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Our Services
          </h2>
          <p className="text-lg text-gray-600">
            Comprehensive solar energy solutions tailored to meet your residential, commercial, and industrial needs
          </p>
        </div>

        {/* Services Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 mb-12 bg-gray-100 p-1 rounded-lg">
            <TabsTrigger
              value="installation"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-lime-500 data-[state=active]:to-green-600 data-[state=active]:text-white text-gray-700 font-semibold py-3 rounded-md transition-all"
            >
              Installation Services
            </TabsTrigger>
            <TabsTrigger
              value="engineering"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-lime-500 data-[state=active]:to-green-600 data-[state=active]:text-white text-gray-700 font-semibold py-3 rounded-md transition-all"
            >
              Engineering & Consulting
            </TabsTrigger>
            <TabsTrigger
              value="maintenance"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-lime-500 data-[state=active]:to-green-600 data-[state=active]:text-white text-gray-700 font-semibold py-3 rounded-md transition-all"
            >
              Maintenance & Support
            </TabsTrigger>
            <TabsTrigger
              value="components"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-lime-500 data-[state=active]:to-green-600 data-[state=active]:text-white text-gray-700 font-semibold py-3 rounded-md transition-all"
            >
              Technical Components
            </TabsTrigger>
          </TabsList>

          {/* Installation Services Tab */}
          <TabsContent value="installation" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <ServiceCard service={mockData.services.installation[0]} IconComponent={Home} />
              <ServiceCard service={mockData.services.installation[1]} IconComponent={Building} />
              <ServiceCard service={mockData.services.installation[2]} IconComponent={Zap} />
              <ServiceCard service={mockData.services.installation[3]} IconComponent={Battery} />
              <ServiceCard service={mockData.services.installation[4]} IconComponent={Layers} />
              <ServiceCard service={mockData.services.installation[5]} IconComponent={Sun} />
              <ServiceCard service={mockData.services.installation[6]} IconComponent={Map} />
              <ServiceCard service={mockData.services.installation[7]} IconComponent={Grid} />
            </div>
          </TabsContent>

          {/* Engineering Services Tab */}
          <TabsContent value="engineering" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <ServiceCard service={mockData.services.engineering[0]} IconComponent={Search} />
              <ServiceCard service={mockData.services.engineering[1]} IconComponent={Layout} />
              <ServiceCard service={mockData.services.engineering[2]} IconComponent={FileText} />
              <ServiceCard service={mockData.services.engineering[3]} IconComponent={CheckCircle} />
              <ServiceCard service={mockData.services.engineering[4]} IconComponent={DollarSign} />
            </div>
          </TabsContent>

          {/* Maintenance Services Tab */}
          <TabsContent value="maintenance" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <ServiceCard service={mockData.services.maintenance[0]} IconComponent={Settings} />
              <ServiceCard service={mockData.services.maintenance[1]} IconComponent={Droplet} />
              <ServiceCard service={mockData.services.maintenance[2]} IconComponent={Wrench} />
              <ServiceCard service={mockData.services.maintenance[3]} IconComponent={Activity} />
            </div>
          </TabsContent>

          {/* Components Tab */}
          <TabsContent value="components" className="mt-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <ServiceCard service={mockData.services.components[0]} IconComponent={Grid} />
              <ServiceCard service={mockData.services.components[1]} IconComponent={Cpu} />
              <ServiceCard service={mockData.services.components[2]} IconComponent={BatteryCharging} />
              <ServiceCard service={mockData.services.components[3]} IconComponent={Box} />
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <Card className="max-w-4xl mx-auto bg-gradient-to-r from-lime-500 to-green-600 border-none shadow-2xl">
            <CardContent className="p-12">
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Switch to Solar Energy?
              </h3>
              <p className="text-white/90 text-lg mb-6">
                Get a free consultation and quote for your solar energy needs today
              </p>
              <button
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="bg-white text-green-700 font-semibold px-8 py-4 rounded-lg hover:bg-gray-50 transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                Contact Us Now
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Services;
